import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { SERVICE_UNITS } from '../constants';
import { createCategoryName, resolveCategoryLabel, searchCategories } from '../select';
import type { ServiceInput } from '../types';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  category: z.string(),
  description: z.string(),
  unit: z.enum(['hourly', 'fixed', 'milestone', 'retainer']),
  base_rate: z.string().refine((v) => v !== '' && Number(v) >= 0, 'Enter a rate ≥ 0'),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['name', 'category', 'description', 'unit', 'base_rate', 'is_active'] as const;

interface ServiceFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (values: ServiceInput) => Promise<unknown>;
  onCancel: () => void;
}

export function ServiceForm({ defaultValues, submitLabel, onSubmit, onCancel }: ServiceFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      category: '',
      description: '',
      unit: 'hourly',
      base_rate: '',
      is_active: true,
      ...defaultValues,
    },
  });

  async function handleSubmit(values: FormValues) {
    const input: ServiceInput = {
      name: values.name,
      category: values.category ? Number(values.category) : null,
      description: values.description,
      unit: values.unit,
      base_rate: values.base_rate.trim(),
      is_active: values.is_active,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-5">
        <FormSection
          variant="card"
          title="Service"
          description="What is this service and how is it categorized?"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Senior Engineering" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <FormControl>
                  <EntitySelect
                    queryKey="service-category-picker"
                    value={field.value}
                    onChange={field.onChange}
                    search={searchCategories}
                    resolveLabel={resolveCategoryLabel}
                    onCreate={createCategoryName}
                    placeholder="No category"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection
          variant="card"
          title="Pricing"
          description="How it's billed and the base USD rate."
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SERVICE_UNITS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="base_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Base rate (USD)
                    <InfoHint text="Per-currency prices are added as overrides." />
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min={0} step="0.01" placeholder="150.00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          variant="card"
          title="Status"
          description="Control whether this service is offered."
        >
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="flex items-center gap-1.5">
                    Active
                    <InfoHint text="Inactive services are hidden from pickers." />
                  </FormLabel>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
